import { createElement, lazy, Suspense } from 'react';
import { usesDesignV2 } from '../features/design-v2/rollout';
import type { PageContext } from '../lib/types';
// Projects (IA §37): global list with views + filters. No folders, no sub-projects (IA §7).
import { html, raw, toast } from '../lib/dom';
import { setQuery } from '../lib/router';
import * as store from '../store/ui';
import { icon } from '../components/icons';
import { coverThumb } from '../components/thumb';
import { projectCard, pageHead, tabs, select, searchInput, status, table, favButton, empty } from '../components/ui';
import { openModal } from '../components/modal';
import { listProjects, listWorkspaces, getWorkspace, listBrands, projectBrands, projectStats, creators, createProject, createWorkspace, currentUser, getUser } from '../api/workspace';
import { loadTable } from '../api/core';
import { relTime } from '../lib/format';

const ProjectsPage = lazy(() => import('../features/design-v2/ProjectsPage').then(module => ({default:module.ProjectsPage})));

const VIEWS = [['all', 'All'], ['recent', 'Recent'], ['favorites', 'Favorites'], ['active', 'Active'], ['archived', 'Archived']];
const STATUSES = [['active', 'Active'], ['in_production', 'In production'], ['archived', 'Archived']];

export async function openNewProject({ workspaceId, brandId, returnFocus, initialDescription = '', designV2 = false }: any = {}) {
  try { await Promise.all([loadTable('workspaces'), loadTable('brands')]); }
  catch (error) { toast(error instanceof Error ? error.message : 'Cannot load workspaces'); return; }
  const workspaces = listWorkspaces();
  openModal({
    title: 'New project',
    returnFocus: () => returnFocus?.isConnected ? returnFocus : document.querySelector<HTMLElement>('[data-new-project]'),
    body: html`<form class="form" data-new-project-form>
      <div class="field"><label>Name</label><input class="input" name="name" placeholder="e.g. Koloblox Holiday Campaign" required autofocus></div>
      <div class="form-row">
        ${workspaces.length ? html`<div class="field"><label>Workspace</label><select class="select" name="workspace" required>${workspaces.map((w?: any) => html`<option value="${w.id}" ${raw(w.id === workspaceId ? 'selected' : '')}>${w.name}</option>`)}</select></div>` : html`<div class="field"><label>New workspace name</label><input class="input" name="workspaceName" value="My workspace" required><span class="hint">Your first workspace will be created with this project.</span></div>`}
        <div class="field"><label>Brand</label><select class="select" name="brand"><option value="">No brand</option>${listBrands().map((b?: any) => html`<option value="${b.id}" ${raw(b.id === brandId ? 'selected' : '')}>${b.name}</option>`)}</select></div>
      </div>
      <div class="field"><label>Description</label><textarea class="textarea" name="description" rows="3">${initialDescription}</textarea></div>
      <p role="alert" data-create-error hidden></p>
      <p class="muted" style="font-size:12px">Projects belong to one workspace and can’t be nested. Use brand, tags and favorites to organise them.</p>
    </form>`,
    foot: html`<button class="btn ghost" data-close>Cancel</button><button class="btn primary" data-create>Create project</button>`,
    onMount(m?: any, close?: any) {
      const form = m.querySelector('form');
      const button = m.querySelector('[data-create]');
      const errorBox = m.querySelector('[data-create-error]');
      let pending = false;
      let createdWorkspaceId: string | undefined;
      const submit = async (e?: any) => {
        e?.preventDefault();
        if (pending || !form.reportValidity()) return;
        const f = new FormData(form);
        if (!String(f.get('name') || '').trim()) { form.elements.name.focus(); return; }
        pending = true; button.disabled = true; button.textContent = 'Creating…'; errorBox.hidden = true;
        try {
          const selectedWorkspace = f.get('workspace') || createdWorkspaceId || await createWorkspace({ name: f.get('workspaceName'), userId: currentUser().id });
          createdWorkspaceId = String(selectedWorkspace);
          const id = await createProject({ name: String(f.get('name')).trim(), workspaceId: selectedWorkspace, brandId: f.get('brand') || null, description: f.get('description'), userId: currentUser().id });
          close(); location.hash = `/projects/${id}${designV2 ? '?design=v2' : ''}`;
        } catch (error) {
          errorBox.textContent = error instanceof Error ? error.message : 'Project could not be saved. Please try again.';
          errorBox.hidden = false;
        } finally { pending = false; button.disabled = false; button.textContent = 'Create project'; }
      };
      form.addEventListener('submit', submit);
      m.querySelector('[data-create]').addEventListener('click', submit);
    },
  });
}

export function render(ctx: PageContext) {
  if (usesDesignV2(ctx)) return { title: 'Projects', content: createElement(Suspense, {fallback:createElement('div', {role:'status', className:'page'}, 'Loading projects…')}, createElement(ProjectsPage, { context: ctx, onCreate: (returnFocus: HTMLElement | null) => { void openNewProject({ workspaceId:ctx.workspaceId || ctx.query.workspace || null, returnFocus, designV2:true }); } })) };
  const q = ctx.query;
  const s = store.get();
  const view = VIEWS.some(([key]) => key === q.view) ? q.view : 'all';
  const layout = q.layout || 'grid';
  const workspaceId = ctx.workspaceId || q.workspace || null;
  const base: any = { workspaceId, brandId: q.brand, status: q.status, creator: q.creator, q: q.q, favorites: s.favorites, recents: s.recents };
  const rows = listProjects({ ...base, view });
  const counts = Object.fromEntries(VIEWS.map(([k]: any) => [k, listProjects({ ...base, view: k }).length]));
  const ctxWs = ctx.workspaceId && getWorkspace(ctx.workspaceId);

  const body = html`<div class="page">
    ${pageHead({ title: ctxWs ? 'Projects' : 'All projects', lede: ctxWs ? `Projects in ${ctxWs.name}.` : 'Organize your projects and flows.', actions: html`<button class="btn primary" data-new-project>${icon('plus')}New project</button>` })}
    ${tabs(VIEWS.map(([k, l]: any) => [k, l, counts[k]]), view, { attr: 'data-view' })}
    <div class="toolbar">
      ${searchInput(q.q, 'Search projects…')}
      ${ctxWs
        ? html`<span class="chip">${ctxWs.name}<button class="icon-btn" style="width:18px;height:18px" data-set-ws="" title="Show all workspaces">${icon('close')}</button></span>`
        : select('workspace', listWorkspaces().map((w?: any) => [w.id, w.name]), q.workspace, { placeholder: 'Any workspace' })}
      ${select('brand', listBrands().map((b?: any) => [b.id, b.name]), q.brand, { placeholder: 'Any brand' })}
      ${select('status', STATUSES, q.status, { placeholder: 'Any status' })}
      ${select('creator', creators().map((u?: any) => [u.id, u.name]), q.creator, { placeholder: 'Any creator' })}
      <span class="spacer"></span>
      <div class="seg" role="group" aria-label="Layout"><button class="${layout === 'grid' ? 'on' : ''}" data-param="layout" data-value="grid">${icon('grid')}</button><button class="${layout === 'table' ? 'on' : ''}" data-param="layout" data-value="table">${icon('list')}</button></div>
    </div>
    ${!rows.length ? empty(view === 'favorites' ? 'Star a project to add it here.' : 'No projects match these filters.')
      : layout === 'grid'
        ? html`<div class="grid auto">${rows.map((p?: any) => projectCard(p, { workspace: getWorkspace(p.workspace_id), brands: projectBrands(p.id), stats: projectStats(p.id) }))}</div>`
        : html`<div class="card">${table([
          { label: 'Project', render: (p?: any) => html`<div class="cell-entity">${coverThumb(p)}<div class="col" style="gap:0"><strong style="font-weight:500">${p.name}</strong><span class="muted ellipsis" style="font-size:12px;max-width:360px">${p.description}</span></div></div>` },
          { label: 'Workspace', render: (p?: any) => getWorkspace(p.workspace_id)?.name },
          { label: 'Brand', render: (p?: any) => projectBrands(p.id).map((b?: any) => html`<span class="chip">${b.name}</span>`) },
          { label: 'Status', render: (p?: any) => status(p.status) },
          { label: 'Flows', num: true, render: (p?: any) => projectStats(p.id).flows },
          { label: 'Executions', num: true, render: (p?: any) => projectStats(p.id).runs },
          { label: 'Creator', render: (p?: any) => getUser(p.created_by)?.name },
          { label: 'Updated', render: (p?: any) => relTime(p.updated_at) },
          { label: '', render: (p?: any) => favButton('project', p.id) },
        ], rows, { rowHref: (p?: any) => `#/projects/${p.id}` })}</div>`}
  </div>`;

  return {
    title: 'Projects', body,
    mount(root?: any) {
      const open = (event?: MouseEvent) => openNewProject({ workspaceId, returnFocus: event?.currentTarget });
      root.querySelector('[data-new-project]')?.addEventListener('click', open);
      if (q.new === '1') { setQuery({ new: null }); open(); }
    },
  };
}
